import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Invitation } from "@models/invitation";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorState } from "primeng/paginator";
import {
  combineLatest,
  debounceTime,
  finalize,
  startWith,
  Subject,
  switchMap,
} from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { EventsManagementService } from "../../../services/events-management.service";
import { InvitationsService } from "../../../services/invitations.service";
import { ConfirmActionModalComponent } from "../../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import {
  AddGuestDialogComponent,
  AddGuestDialogData,
} from "./add-guest-dialog/add-guest-dialog.component";
import { GuestsTableComponent } from "./guests-table/guests-table.component";

@Component({
  selector: "app-guests-tab",
  imports: [
    CommonModule,
    GuestsTableComponent,
    ReactiveFormsModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    ButtonComponent,
  ],
  templateUrl: "./guests-tab.component.html",
  styleUrl: "./guests-tab.component.scss",
})
export class GuestsTabComponent {
  @Input() eventId: number | null = null;
  @Input() readOnly = false;
  tableFilter = new FormControl("", { nonNullable: true });

  private readonly _dialog = inject(MatDialog);
  private readonly _eventsService = inject(EventsManagementService);
  private readonly _invitationsService = inject(InvitationsService);

  private readonly refreshInvitations$ = new Subject<void>();
  private readonly page$ = new Subject<PaginatorState>();

  loading = false;

  invitations$ = combineLatest([
    this.page$.pipe(startWith(null)),
    this.tableFilter.valueChanges.pipe(startWith(""), debounceTime(500)),
    this.refreshInvitations$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([page, search]) =>
      this._eventsService.getInvitationsByEventId(this.eventId!, page, search),
    ),
  );

  adicionarConvidado() {
    const dialogRef = this._dialog.open(AddGuestDialogComponent, {
      width: "640px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      data: { eventId: this.eventId } as AddGuestDialogData,
    });

    dialogRef.afterClosed().subscribe((guestAdded) => {
      if (guestAdded) {
        console.log("Deve atualizar a tabela");
        this.tableFilter.setValue("");
        this.refreshInvitations$.next();
      }
    });
  }

  handleEditParticipant(participant: Invitation) {
    if (!this.eventId) {
      return;
    }
    const addGuestDialogData: AddGuestDialogData = {
      eventId: this.eventId,
      participant,
    };
    const dialogRef = this._dialog.open(AddGuestDialogComponent, {
      width: "640px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      data: addGuestDialogData,
    });

    dialogRef.afterClosed().subscribe((guestAdded) => {
      if (guestAdded) {
        this.tableFilter.setValue("");
        this.refreshInvitations$.next();
      }
    });
  }

  handleResendInvite(participant: Invitation) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Reenviar convite?",
        modalContent: `Confirma que deseja reenviar o convite para esse convidado? </br> O e-mail <b>${participant.user_email}</b> está correto?`,
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this._eventsService
          .resendInvite(Number(participant.event.id), participant.id)
          .subscribe();
      }
    });
  }

  handleCancelParticipant(invitation: Invitation) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Remover convidado?",
        modalContent: "Deseja realmente cancelar o convite dessa pessoa?",
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this._invitationsService
          .deleteInvitations(invitation.id)
          .subscribe(() => {
            this.refreshInvitations$.next();
          });
      }
    });
  }

  handlePageChange(page: PaginatorState) {
    this.page$.next(page);
  }

  downloadReport() {
    if (!this.eventId) return;
    this.loading = true;
    this._eventsService
      .downloadCortesiasReport(this.eventId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "cortesias.pdf";
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }
}
